import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
// Simple zoom functionality without external libraries
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} previousButton - The previous navigation button.
 * @property {HTMLButtonElement} nextButton - The next navigation button.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  optionalRefs = ['previousButton', 'nextButton'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #currentIndex = 0;
  #dragging = false;
  #isOpening = false;
  #startX = 0;
  #startY = 0;
  #scrollLeft = 0;
  #hasDragged = false;
  // Simple zoom state tracking

  connectedCallback() {
    super.connectedCallback();
    
    const gallery = this.querySelector('.dialog-zoomed-gallery');
    if (gallery) {
      gallery.addEventListener('mousedown', this.#handleMouseDown);
      gallery.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    }
    this.updateArrowStates();
  }
  
  /**
   * Initialize PinchZoom on all images INSIDE the dialog only
   */
  #initializePinchZoom = () => {
    // Simple click-to-zoom functionality without PinchZoom
    const dialog = this.refs.dialog;
    const images = dialog.querySelectorAll('.product-media__drag-zoom-wrapper img');
    
    images.forEach((img) => {
      if (img.dataset.zoomInitialized) return;
      img.dataset.zoomInitialized = 'true';
      
      const wrapper = img.parentElement;
      let isZoomed = false;
      
      let hasDragged = false;
      
      img.addEventListener('click', (e) => {
        // Don't zoom if we just dragged
        if (hasDragged) {
          hasDragged = false;
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        const rect = wrapper.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        if (!isZoomed) {
          // Zoom in with smooth transition
          wrapper.classList.add('is-zoomed');
          img.style.transition = 'transform 0.3s ease-out';
          img.style.transformOrigin = `${e.clientX - rect.left}px ${e.clientY - rect.top}px`;
          img.style.transform = 'scale(2)';
          isZoomed = true;
          
          // Remove transition after animation completes
          setTimeout(() => {
            img.style.transition = 'none';
          }, 300);
        } else {
          // Zoom out with smooth transition
          wrapper.classList.remove('is-zoomed');
          img.style.transition = 'transform 0.3s ease-out';
          img.style.transformOrigin = 'center center';
          img.style.transform = 'scale(1)';
          isZoomed = false;
          
          // Remove transition after animation completes
          setTimeout(() => {
            img.style.transition = 'none';
          }, 300);
        }
      });
      
      // Pan when zoomed
      let isPanning = false;
      let startX = 0;
      let startY = 0;
      let startTransform = '';
      
      img.addEventListener('mousedown', (e) => {
        if (!isZoomed) return;
        isPanning = true;
        hasDragged = false;
        startX = e.clientX;
        startY = e.clientY;
        startTransform = img.style.transform;
        wrapper.style.cursor = 'grabbing';
        e.preventDefault();
      });
      
      const handleMouseMove = (e) => {
        if (!isPanning || !isZoomed) return;
        
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        
        // Set hasDragged if moved more than 5 pixels
        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          hasDragged = true;
        }
        
        const currentScale = isZoomed ? 2 : 1;
        const newTransform = `scale(${currentScale}) translate(${deltaX / currentScale}px, ${deltaY / currentScale}px)`;
        img.style.transform = newTransform;
      };
      
      const handleMouseUp = () => {
        if (isPanning) {
          isPanning = false;
          wrapper.style.cursor = '';
        }
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    
    const gallery = this.querySelector('.dialog-zoomed-gallery');
    if (gallery) {
      gallery.removeEventListener('mousedown', this.#handleMouseDown);
      gallery.removeEventListener('touchstart', this.#handleTouchStart);
    }
    
    // Clean up zoom functionality
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The pointer event (optional).
   */
  async open(index, event) {
    // Prevent duplicate calls
    if (this.#isOpening) {
      return;
    }
    this.#isOpening = true;

    if (event) {
      event.preventDefault();
    }

    this.#currentIndex = index;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      // Initialize zoom functionality now that dialog is open
      this.#initializePinchZoom();

            // Add scroll event listener to the gallery now that it's open
            const gallery = this.querySelector('.dialog-zoomed-gallery');
            if (gallery) {
              gallery.addEventListener('scroll', this.handleScroll);
            }

            for (const target of [targetThumbnail, targetImage]) {
              if (target) {
                target.scrollIntoView({ behavior: 'instant' });
              }
            }
    };

    return open();
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#currentIndex = activeIndex;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.updateArrowStates();
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Navigate to the next media item
   */
  next(event) {
    event?.preventDefault();
    const { media } = this.refs;
    if (!media || media.length === 0) return;

    let nextIndex = this.#currentIndex + 1;
    
    // Infinite loop: wrap to first if at the end
    if (nextIndex >= media.length) {
      nextIndex = 0;
    }

    this.selectThumbnail(nextIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Navigate to the previous media item
   */
  previous(event) {
    event?.preventDefault();
    const { media } = this.refs;
    if (!media || media.length === 0) return;

    let prevIndex = this.#currentIndex - 1;
    
    // Infinite loop: wrap to last if at the beginning
    if (prevIndex < 0) {
      prevIndex = media.length - 1;
    }

    this.selectThumbnail(prevIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Update arrow button states (disabled when can't navigate)
   */
  updateArrowStates() {
    const { previousButton, nextButton, media } = this.refs;
    
    // With infinite loop, arrows are never disabled
    if (previousButton) previousButton.disabled = false;
    if (nextButton) nextButton.disabled = false;
  }


  /**
   * Handle mouse down for drag functionality
   */
  #handleMouseDown = (event) => {
    // Don't interfere with buttons or thumbnails
    if (event.target.closest('.dialog-zoom-arrow, .close-button, .dialog-thumbnails-list')) return;
    
    // Don't interfere with PinchZoom
    const zoomWrapper = event.target.closest('.product-media__drag-zoom-wrapper');
    if (zoomWrapper) return;
    
    const gallery = this.querySelector('.dialog-zoomed-gallery');
    if (!gallery) return;
    
    this.#dragging = true;
    this.#hasDragged = false;
    this.#startX = event.pageX;
    this.#startY = event.pageY;
    this.#scrollLeft = gallery.scrollLeft;
    
    gallery.style.cursor = 'grabbing';
    gallery.style.userSelect = 'none';
    
    const handleMouseMove = (e) => {
      if (!this.#dragging) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const deltaX = e.pageX - this.#startX;
      const deltaY = e.pageY - this.#startY;
      
      // If moved more than 5px, consider it a drag
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.#hasDragged = true;
      }
      
      const walk = deltaX * 1.5;
      gallery.scrollLeft = this.#scrollLeft - walk;
    };
    
    const handleMouseUp = (e) => {
      this.#dragging = false;
      gallery.style.cursor = '';
      gallery.style.userSelect = '';
      
      // Check for infinite loop at edges
      const deltaX = e.pageX - this.#startX;
      const atStart = gallery.scrollLeft <= 0;
      const atEnd = gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 1;
      const draggedRight = deltaX > 0;
      const draggedLeft = deltaX < 0;
      
      // If at edge and dragged in that direction, trigger infinite loop
      if (this.#hasDragged && Math.abs(deltaX) > 30) {
        if (atStart && draggedRight) {
          this.previous();
          this.#hasDragged = false;
        } else if (atEnd && draggedLeft) {
          this.next();
          this.#hasDragged = false;
        }
      }
      
      // If dragged, prevent the click event from closing the dialog
      if (this.#hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        
        // Capture and stop the next click event
        const preventClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          gallery.removeEventListener('click', preventClick, true);
        };
        gallery.addEventListener('click', preventClick, true);
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Handle touch start for mobile swipe functionality
   */
  #handleTouchStart = (event) => {
    // Don't interfere with buttons or thumbnails
    if (event.target.closest('.dialog-zoom-arrow, .close-button, .dialog-thumbnails-list')) return;
    
    const gallery = this.querySelector('.dialog-zoomed-gallery');
    if (!gallery) return;
    
    this.#hasDragged = false;
    this.#startX = event.touches[0].pageX;
    this.#startY = event.touches[0].pageY;
    this.#scrollLeft = gallery.scrollLeft;
    
    const handleTouchMove = (e) => {
      const deltaX = e.touches[0].pageX - this.#startX;
      const deltaY = e.touches[0].pageY - this.#startY;
      
      // If moved more than 5px, consider it a drag
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.#hasDragged = true;
      }
      
      const walk = deltaX * 1.5;
      gallery.scrollLeft = this.#scrollLeft - walk;
    };
    
    const handleTouchEnd = (e) => {
      // Check for infinite loop at edges
      const deltaX = e.changedTouches[0].pageX - this.#startX;
      const atStart = gallery.scrollLeft <= 0;
      const atEnd = gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 1;
      const draggedRight = deltaX > 0;
      const draggedLeft = deltaX < 0;
      
      // If at edge and dragged in that direction, trigger infinite loop
      if (this.#hasDragged && Math.abs(deltaX) > 30) {
        if (atStart && draggedRight) {
          this.previous();
          this.#hasDragged = false;
        } else if (atEnd && draggedLeft) {
          this.next();
          this.#hasDragged = false;
        }
      }
      
      // If dragged, prevent the click event
      if (this.#hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        
        // Capture and stop the next click event
        const preventClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          gallery.removeEventListener('click', preventClick, true);
        };
        gallery.addEventListener('click', preventClick, true);
      }
      
      gallery.removeEventListener('touchmove', handleTouchMove);
      gallery.removeEventListener('touchend', handleTouchEnd);
    };
    
    gallery.addEventListener('touchmove', handleTouchMove, { passive: true });
    gallery.addEventListener('touchend', handleTouchEnd);
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }


  closeDialog() {
    const { dialog } = this.refs;
    
    // Remove scroll event listener when closing
    const gallery = this.querySelector('.dialog-zoomed-gallery');
    if (gallery) {
      gallery.removeEventListener('scroll', this.handleScroll);
    }
    
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
    this.#isOpening = false;
  }

  /**
   * Handles keyboard navigation in the dialog.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous(event);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next(event);
      return;
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Reset zoom on all images
   */
  #resetAllZoom() {
    // Reset all zoomed images
    const dialog = this.refs.dialog;
    const images = dialog.querySelectorAll('.product-media__drag-zoom-wrapper img');
    images.forEach((img) => {
      const wrapper = img.parentElement;
      wrapper.classList.remove('is-zoomed');
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
    });
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#currentIndex = index;
    
    // Reset zoom when changing images
    this.#resetAllZoom();

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    
    this.updateArrowStates();
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
